import { createLogger, Logger, LoggerLevel } from '@quickplay/foundation';
import { DrmScheme, MediaType, Player, PlaybackOptions } from '@quickplay/player-interface';
import { ExternalPlayer } from '../ExternalPlayer';
import { PlayerBuilder } from '../PlayerBuilder';
import { createCorePlayer } from './CorePlayer';
import { ThumbnailPreviewConfig } from '../thumbnailPreview/ThumbnailPreviewConfiguration';
import { JanusConfig } from './JanusPlayer/internal/JanusTypes';

const TAG_NAME = 'FL_JANUS_PLAYER';

export interface JanusPlayerBuilder extends PlayerBuilder {
  readonly janusConfig: JanusConfig;
  readonly videoElement: HTMLVideoElement;

  /**
   * @method janusServer
   * @param server Janus Gateway WebSocket server URL
   *
   * Sets the Janus Gateway server URL for WebRTC streaming.
   */
  janusServer(server: string): JanusPlayerBuilder;

  /**
   * @method iceServers
   * @param servers Array of ICE servers for WebRTC connection
   *
   * Sets the ICE servers for WebRTC peer connection.
   */
  iceServers(servers: RTCIceServer[]): JanusPlayerBuilder;

  /**
   * @method streamId
   * @param id Stream identifier for the Janus stream
   *
   * Sets the stream ID for the Janus streaming session.
   */
  streamId(id: number): JanusPlayerBuilder;

  /**
   * @method janusConfiguration
   * @param config Partial Janus configuration object
   *
   * Sets additional Janus configuration options like enableDebug, timeout, etc.
   */
  janusConfiguration(config: Partial<JanusConfig>): JanusPlayerBuilder;

  /**
   * @method usePlayer
   * @param externalPlayer
   *
   * Sets the player type to JanusPlayer.
   */
  usePlayer(externalPlayer: ExternalPlayer): JanusPlayerBuilder;
}

export function CoreJanusPlayerBuilder(): JanusPlayerBuilder {
  let contentUrl!: string;
  let mediaFormatType: MediaType = MediaType.DASH;
  let licenseUrl = '';
  let drmType: DrmScheme = DrmScheme.NONE;
  const options: PlaybackOptions = {
    autoPlayOnLoad: true,
    shouldRetryOnErrors: true,
    maxRetries: 1,
    retiresAttempted: 0,
    repeatWhenFinished: false,
    enforceHDCPCompliance: false,
    initialPlaybackTime: undefined,
    enableEngagementScore: false,
  };
  let videoElement: HTMLVideoElement;
  let extPlayer: ExternalPlayer = ExternalPlayer.JanusPlayer;
  let serverCertificate: ArrayBuffer;
  let sideloadedtracks: any[];
  let defaultLogger: Logger = createLogger(LoggerLevel.OFF, TAG_NAME);
  let config: ThumbnailPreviewConfig;
  let thumbnailPreviewEndpointUrl: string | undefined;
  let webVTTThumbnailPreviewEndpointUrl: string | undefined;
  let library: any;

  // Janus-specific configuration
  const janusConfig: JanusConfig = {
    server: '',
    iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
  };

  return {
    get contentUrl(): string {
      return contentUrl;
    },

    get extPlayer(): ExternalPlayer {
      return extPlayer;
    },

    get mediaFormatType(): MediaType {
      return mediaFormatType;
    },

    get licenseUrl(): string {
      return licenseUrl;
    },

    get drmType(): DrmScheme {
      return drmType;
    },

    get options(): PlaybackOptions {
      return options;
    },

    get configuredLogger(): Logger {
      return defaultLogger;
    },

    get videoElement(): HTMLVideoElement {
      return videoElement;
    },

    get serverCertificate(): ArrayBuffer {
      return serverCertificate;
    },

    get getSideLoadedTracks(): any[] {
      return sideloadedtracks;
    },

    get config(): ThumbnailPreviewConfig {
      return config;
    },

    get thumbnailPreviewEndpointUrl(): string | undefined {
      return thumbnailPreviewEndpointUrl;
    },

    get webVTTThumbnailPreviewEndpointUrl(): string | undefined {
      return webVTTThumbnailPreviewEndpointUrl;
    },

    get janusConfig(): JanusConfig {
      return janusConfig;
    },

    get library(): any {
      return library;
    },

    mediaElement(element: HTMLVideoElement): JanusPlayerBuilder {
      videoElement = element;
      return this;
    },

    mediaWrapperElement(_: HTMLElement): JanusPlayerBuilder {
      // JanusPlayer doesn't use a wrapper element
      return this;
    },

    mediaUrl(mediaUrl: string): JanusPlayerBuilder {
      contentUrl = encodeURI(mediaUrl.trim());
      return this;
    },

    mediaType(type: MediaType): JanusPlayerBuilder {
      mediaFormatType = type;
      return this;
    },

    drmLicenseUrl(drmLicenseUrl: string): JanusPlayerBuilder {
      licenseUrl = drmLicenseUrl;
      return this;
    },

    drmScheme(drmScheme: DrmScheme): JanusPlayerBuilder {
      drmType = drmScheme;
      return this;
    },

    playbackOptions(playbackOptions: PlaybackOptions): JanusPlayerBuilder {
      Object.assign(options, playbackOptions);
      return this;
    },

    usePlayer(externalPlayer: ExternalPlayer): JanusPlayerBuilder {
      if (externalPlayer !== ExternalPlayer.JanusPlayer) {
        throw new Error(
          'JanusPlayerBuilder only supports ExternalPlayer.JanusPlayer. ' +
            'This builder is specifically designed for Janus WebRTC streaming. ' +
            'Please use ExternalPlayer.JanusPlayer or use a different builder for other player types.',
        );
      }
      extPlayer = externalPlayer;
      return this;
    },

    janusServer(server: string): JanusPlayerBuilder {
      janusConfig.server = server;
      return this;
    },

    iceServers(servers: RTCIceServer[]): JanusPlayerBuilder {
      janusConfig.iceServers = servers;
      return this;
    },

    streamId(id: number): JanusPlayerBuilder {
      janusConfig.streamId = id;
      return this;
    },

    janusConfiguration(config: Partial<JanusConfig>): JanusPlayerBuilder {
      Object.assign(janusConfig, config);
      return this;
    },

    certificate(certificate: ArrayBuffer): JanusPlayerBuilder {
      serverCertificate = certificate;
      return this;
    },

    sideCarTracks(vttTracks: any[]): JanusPlayerBuilder {
      sideloadedtracks = vttTracks;
      return this;
    },

    logger(logger: Logger): JanusPlayerBuilder {
      defaultLogger = logger;
      return this;
    },

    enableThumbnailPreview(
      thumbnailPreviewEndpointUrl_: string,
      config_: ThumbnailPreviewConfig = {},
    ): JanusPlayerBuilder {
      thumbnailPreviewEndpointUrl = thumbnailPreviewEndpointUrl_;
      config = config_;
      return this;
    },

    webVTTThumbnailPreview(webVTTTrackUrl: string): JanusPlayerBuilder {
      webVTTThumbnailPreviewEndpointUrl = webVTTTrackUrl;
      return this;
    },

    playerLibrary(playerLib: any): JanusPlayerBuilder {
      library = playerLib;
      return this;
    },

    build(): Player {
      if (!videoElement) {
        throw new Error('Video element is required for JanusPlayer');
      }
      if (!janusConfig.server) {
        throw new Error('Janus server URL is required for JanusPlayer');
      }
      return createCorePlayer(this);
    },
  };
}
